/**
 * 个人设置（/profile，旧 views/system/SettingsView.vue）。
 * 只迁真正生效的部分：基本信息展示、修改密码、主题模式；
 * 旧版里不调接口的"保存"、写死的两步验证/登录会话/通知设置、不生效的主色调选择器不再保留。
 */
import { useState } from 'react'
import { Avatar, Button, Card, Col, Descriptions, Form, Menu, Radio, Row, Typography } from 'antd'
import { SafetyOutlined, SkinOutlined, UserOutlined } from '@ant-design/icons'
import { getRoleDisplayName, getUserDisplayName, getUserInitials, useAuthStore } from '@/stores/auth'
import { useThemeStore, type ThemeMode } from '@/stores/theme'
import { ChangePasswordForm, useSubmitPasswordChange } from '@/layouts/ChangePasswordModal'
import styles from './SettingsPage.module.css'

type SettingsTab = 'profile' | 'security' | 'theme'

const TABS = [
  { key: 'profile', icon: <UserOutlined />, label: '基本信息' },
  { key: 'security', icon: <SafetyOutlined />, label: '安全设置' },
  { key: 'theme', icon: <SkinOutlined />, label: '主题设置' }
]

function ProfileCard() {
  const user = useAuthStore((state) => state.user)
  return (
    <Card variant="borderless" title="基本信息">
      <div className={styles.avatarRow}>
        <Avatar size={100} src={user?.avatar}>
          {getUserInitials(user)}
        </Avatar>
      </div>
      <Descriptions column={1} bordered>
        <Descriptions.Item label="用户名">{user?.username}</Descriptions.Item>
        <Descriptions.Item label="显示名称">{getUserDisplayName(user)}</Descriptions.Item>
        <Descriptions.Item label="邮箱">{user?.email}</Descriptions.Item>
        <Descriptions.Item label="角色">{getRoleDisplayName(user?.role)}</Descriptions.Item>
        <Descriptions.Item label="注册时间">
          {user?.created_at ? new Date(user.created_at).toLocaleString('zh-CN') : '—'}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  )
}

function SecurityCard() {
  const [form] = Form.useForm()
  const { submit, submitting } = useSubmitPasswordChange(form)
  return (
    <Card variant="borderless" title="修改密码">
      <div className={styles.narrowForm}>
        <ChangePasswordForm form={form} />
        <Button type="primary" loading={submitting} onClick={() => void submit()}>
          修改密码
        </Button>
      </div>
    </Card>
  )
}

function ThemeCard() {
  const mode = useThemeStore((state) => state.mode)
  return (
    <Card variant="borderless" title="主题设置">
      <Typography.Paragraph type="secondary">选择后立即生效，并保存在本机。</Typography.Paragraph>
      <Radio.Group
        value={mode}
        onChange={(event) => useThemeStore.getState().setMode(event.target.value as ThemeMode)}
        options={[
          { value: 'light', label: '浅色模式' },
          { value: 'dark', label: '深色模式' },
          { value: 'auto', label: '跟随系统' }
        ]}
      />
    </Card>
  )
}

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile')

  return (
    <div>
      <Typography.Title level={3} className={styles.title}>
        个人设置
      </Typography.Title>
      <Row gutter={[24, 24]}>
        {/* 左侧菜单列至少要放下图标加四个字（如“基本信息”），否则会被截断成省略号 */}
        <Col xs={24} md={8} lg={6} xl={5}>
          <Card variant="borderless" styles={{ body: { padding: 8 } }}>
            <Menu
              mode="vertical"
              items={TABS}
              selectedKeys={[activeTab]}
              onClick={({ key }) => setActiveTab(key as SettingsTab)}
            />
          </Card>
        </Col>
        <Col xs={24} md={16} lg={18} xl={19}>
          {activeTab === 'profile' && <ProfileCard />}
          {activeTab === 'security' && <SecurityCard />}
          {activeTab === 'theme' && <ThemeCard />}
        </Col>
      </Row>
    </div>
  )
}
